import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import kreativGangImage from "../assets/client-logos/kreativ-gang.png"
import solsolImage from "../assets/client-logos/solsol.svg"
import unipasImage from "../assets/client-logos/unipas.svg"
import yuzuImage from "../assets/client-logos/yuzu.png"
import zoozaImage from "../assets/client-logos/zooza.png"
import CrLogo from "../assets/logoCurrentColor.svg?react"
import ladyImage from "../assets/welcomeBg.png"
import {commonTransComponents} from "../utils/i18n.tsx"
import {UnstyledLink} from "./Link.tsx"

// All the following magic CSS numbers were chosen by a scientific process called
// "trying different things until they look nice". Feel free to experiment.

export const HeroLayout: React.FC<{
  children: React.ReactNode
  leftContent?: React.ReactNode
  leftAction?: React.ReactNode
  rightAction?: React.ReactNode
}> = ({
  children,
  leftAction,
  rightAction,
  leftContent = (
    <div
      className={"absolute inset-0 bg-cover bg-center bg-no-repeat"}
      style={{backgroundImage: `url("${ladyImage}")`}}
    />
  ),
}) => {
  return (
    <div
      className={
        "grid grow grid-cols-[0px_1fr_0px] bg-cr-blue-super-light transition-all lg:grid-cols-[50%_minmax(auto,1280px)_auto]"
      }
    >
      {/* Fancy left panel */}
      <div className={"relative isolate flex items-center justify-center bg-transparent p-6"}>
        <div className={"relative h-full w-full overflow-hidden rounded-[40px]"}>
          {/* Background image */}
          {leftContent}

          {/* Content overlay */}
          <div className={"relative z-10 p-8"}>
            <Logo className={"text-cr-white"} />
            <h1 className={"mt-6 font-['Sora'] text-5xl font-bold text-white"}>
              <Trans i18nKey={"HeroLayout_Title"} components={commonTransComponents} />
            </h1>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={"flex flex-col"}>
        <div
          className={twMerge(
            "flex min-h-0 items-start p-7 pt-8 transition-all",
            !leftAction && !rightAction && "lg:hidden"
          )}
        >
          <div className={"mr-10 shrink-0 overflow-hidden transition-all lg:mr-0 lg:w-0"}>
            <Logo className={"text-cr-blue transition-all lg:text-cr-white"} />
          </div>
          <div
            className={twMerge(
              "mt-2 flex h-full grow items-center justify-between gap-10 transition-all max-lg:justify-end",
              !leftAction && "justify-end max-lg:flex-row-reverse max-lg:justify-between"
            )}
          >
            {leftAction && (
              <div className={"text-cr-grey-50"} data-testid={"left-action"}>
                {leftAction}
              </div>
            )}
            {rightAction && (
              <div className={"text-cr-grey-50"} data-testid={"right-action"}>
                {rightAction}
              </div>
            )}
          </div>
        </div>

        <div className={"flex h-full w-full grow flex-col gap-10 py-10"}>
          <div className={"flex grow items-center justify-center"}>{children}</div>
        </div>
      </div>

      {/* Right side filler */}
      <div />
    </div>
  )
}

export const Logo: React.FC<{className?: string; disabled?: boolean}> = ({className, disabled}) => {
  const {t} = useTranslation()

  return (
    <UnstyledLink to={disabled ? undefined : "/"} className={"inline-block"}>
      <CrLogo className={twMerge("w-52", className)} title={t("CloseRocket")} />
    </UnstyledLink>
  )
}

export const Clients: React.FC = () => {
  const {t} = useTranslation()

  return (
    <div className={"absolute right-0 bottom-0 z-10 flex w-full max-w-[700px] flex-col gap-2 p-7 pb-8 transition-all"}>
      <div className={"text-cr-white"}>{t("Clients_TrustedBy")}</div>
      <div className={"flex items-center justify-between brightness-200"}>
        <img src={zoozaImage} className={"h-7"} alt={t("Clients_Zooza")} />
        <img src={solsolImage} className={"h-5"} alt={t("Clients_Solsol")} />
        <img src={kreativGangImage} className={"h-10"} alt={t("Clients_KreativGang")} />
        <img src={unipasImage} className={"h-6"} alt={t("Clients_Unipas")} />
        <img src={yuzuImage} className={"h-10"} alt={t("Clients_Yuzu")} />
      </div>
    </div>
  )
}
